/**
 * UpsellsTab - Admin UI for Managing Upsell Rules
 * 
 * Features:
 * - Define anchor items that trigger upsells
 * - Link specific upsell items to each anchor
 * - Control frequency (every add, first add, once per session)
 * - Set priorities when multiple anchors in cart
 * - Category fallback rules as backup
 * - Global settings control
 * - Analytics for conversion tracking
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Sparkles, Plus, Trash2, Edit2, Save, X, ChevronDown, ChevronUp,
  Settings2, Package, AlertCircle, Check, Search, ArrowUp, ArrowDown,
  Coffee, ToggleLeft, ToggleRight, HelpCircle, Layers, RefreshCw,
  Info, Loader2, GripVertical, ShoppingCart, TrendingUp, BarChart3,
  Eye, MousePointerClick, Target, Calendar, Lightbulb, Zap, ThumbsUp,
  ThumbsDown, Wand2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

// Frequency options
const FREQUENCY_OPTIONS = [
  { value: 'every_add', label: 'Every Time Added', desc: 'Show upsell popup every time this item is added to cart' },
  { value: 'first_add_only', label: 'First Add Only', desc: 'Show only the first time this item is added in a session' },
  { value: 'once_per_session', label: 'Once Per Session', desc: 'Show once per browsing session, even if item added multiple times' }
];

// Tab navigation
const TABS = [
  { id: 'item-rules', label: 'Item Rules', icon: Package },
  { id: 'suggestions', label: 'AI Suggestions', icon: Sparkles },
  { id: 'category-rules', label: 'Category Fallbacks', icon: Layers },
  { id: 'analytics', label: 'Analytics', icon: TrendingUp },
  { id: 'settings', label: 'Settings', icon: Settings2 },
  { id: 'help', label: 'How It Works', icon: HelpCircle }
];

export default function UpsellsTab() {
  const [activeTab, setActiveTab] = useState('item-rules');
  const [loading, setLoading] = useState(true);
  const [menuItems, setMenuItems] = useState([]);
  const [rules, setRules] = useState([]);
  const [categoryRules, setCategoryRules] = useState([]);
  const [settings, setSettings] = useState({});
  const [categories, setCategories] = useState([]);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [menuRes, rulesRes, catRulesRes, settingsRes] = await Promise.all([
        api.get('/menu'),
        api.get('/admin/upsells/rules'),
        api.get('/admin/upsells/category-rules'),
        api.get('/admin/upsells/settings')
      ]);

      // Menu API returns array directly or {items: [...]}
      const menuData = menuRes.data;
      const items = Array.isArray(menuData) ? menuData : (menuData.items || []);
      setMenuItems(items);
      // Extract unique categories
      const cats = [...new Set(items.map(i => i.category).filter(Boolean))];
      setCategories(cats);
      
      setRules(rulesRes.data.rules || []);
      setCategoryRules(catRulesRes.data.rules || []);
      setSettings(settingsRes.data);
    } catch (err) {
      toast.error('Failed to load upsell data');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter menu items for search
  const filteredMenuItems = menuItems.filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get items that already have rules
  const itemsWithRules = new Set(rules.map(r => r.anchor_item_id));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="ml-3 text-muted-foreground">Loading upsell configuration...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-amber-500" />
            Upsell Management
          </h2>
          <p className="text-muted-foreground mt-1">
            Configure which items trigger upsell suggestions and what gets recommended
          </p>
        </div>
        <Button onClick={loadData} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-border pb-2 overflow-x-auto">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'item-rules' && (
        <ItemRulesTab
          rules={rules}
          setRules={setRules}
          menuItems={menuItems}
          itemsWithRules={itemsWithRules}
          onRefresh={loadData}
        />
      )}

      {activeTab === 'suggestions' && (
        <SuggestionsTab 
          onRuleCreated={(newRule) => {
            setRules([...rules, newRule]);
          }}
        />
      )}

      {activeTab === 'category-rules' && (
        <CategoryRulesTab
          categoryRules={categoryRules}
          setCategoryRules={setCategoryRules}
          categories={categories}
          onRefresh={loadData}
        />
      )}

      {activeTab === 'analytics' && (
        <AnalyticsTab />
      )}

      {activeTab === 'settings' && (
        <SettingsTab
          settings={settings}
          setSettings={setSettings}
        />
      )}

      {activeTab === 'help' && <HelpTab />}
    </div>
  );
}

// ============================================================================
// Item Rules Tab
// ============================================================================
function ItemRulesTab({ rules, setRules, menuItems, itemsWithRules, onRefresh }) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const deleteRule = async (anchorItemId) => {
    if (!confirm('Are you sure you want to delete this upsell rule?')) return;
    
    try {
      await api.delete(`/admin/upsells/rules/${anchorItemId}`);
      setRules(rules.filter(r => r.anchor_item_id !== anchorItemId));
      toast.success('Upsell rule deleted');
    } catch (err) {
      toast.error('Failed to delete rule');
    }
  };

  const toggleRule = async (rule) => {
    try {
      await api.put(`/admin/upsells/rules/${rule.anchor_item_id}`, { is_active: !rule.is_active });
      setRules(rules.map(r => 
        r.anchor_item_id === rule.anchor_item_id 
          ? { ...r, is_active: !r.is_active }
          : r
      ));
      toast.success(rule.is_active ? 'Rule disabled' : 'Rule enabled');
    } catch (err) {
      toast.error('Error updating rule');
    }
  };

  const filteredRules = rules.filter(rule =>
    rule.anchor_item_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-4">
      {/* Actions Bar */}
      <div className="flex flex-col sm:flex-row gap-3 justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search rules..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button onClick={() => setShowAddModal(true)} className="bg-amber-500 hover:bg-amber-600">
          <Plus className="w-4 h-4 mr-2" />
          Add Upsell Rule
        </Button>
      </div>

      {/* Rules List */}
      {filteredRules.length === 0 ? (
        <div className="text-center py-12 bg-muted/30 rounded-xl border border-dashed">
          <Sparkles className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No Upsell Rules Yet</h3>
          <p className="text-muted-foreground mb-4">
            Create rules to suggest specific items when customers add certain products to their cart
          </p>
          <Button onClick={() => setShowAddModal(true)} variant="outline">
            <Plus className="w-4 h-4 mr-2" />
            Create Your First Rule
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredRules.map((rule) => (
            <RuleCard
              key={rule.anchor_item_id}
              rule={rule}
              menuItems={menuItems}
              onEdit={() => setEditingRule(rule)}
              onDelete={() => deleteRule(rule.anchor_item_id)}
              onToggle={() => toggleRule(rule)}
            />
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {(showAddModal || editingRule) && (
        <RuleModal
          rule={editingRule}
          menuItems={menuItems}
          itemsWithRules={itemsWithRules}
          onClose={() => {
            setShowAddModal(false);
            setEditingRule(null);
          }}
          onSave={(savedRule) => {
            if (editingRule) {
              setRules(rules.map(r => 
                r.anchor_item_id === savedRule.anchor_item_id ? savedRule : r
              ));
            } else {
              setRules([...rules, savedRule]);
            }
            setShowAddModal(false);
            setEditingRule(null);
          }}
        />
      )}
    </div>
  );
}

// Rule Card Component
function RuleCard({ rule, menuItems, onEdit, onDelete, onToggle }) {
  const [expanded, setExpanded] = useState(false);
  const anchorItem = menuItems.find(i => i.id === rule.anchor_item_id);

  return (
    <div className={`bg-card rounded-xl border shadow-sm overflow-hidden ${!rule.is_active ? 'opacity-60' : ''}`}>
      {/* Header */}
      <div 
        className="p-4 flex items-center gap-4 cursor-pointer hover:bg-muted/30"
        onClick={() => setExpanded(!expanded)}
      >
        {anchorItem?.image && (
          <img src={anchorItem.image} alt="" className="w-14 h-14 rounded-lg object-cover" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-foreground truncate">{rule.anchor_item_name}</h3>
            {!rule.is_active && (
              <span className="text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded">
                Disabled
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {rule.upsell_items?.length || 0} upsell items • "{rule.message}"
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            title={rule.is_active ? 'Disable' : 'Enable'}
          >
            {rule.is_active ? (
              <ToggleRight className="w-5 h-5 text-green-500" />
            ) : (
              <ToggleLeft className="w-5 h-5 text-gray-400" />
            )}
          </Button>
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); onEdit(); }}>
            <Edit2 className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); onDelete(); }}>
            <Trash2 className="w-4 h-4 text-red-500" />
          </Button>
          {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-border pt-4 space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Frequency:</span>
              <p className="font-medium">{FREQUENCY_OPTIONS.find(f => f.value === rule.frequency)?.label || rule.frequency}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Priority:</span>
              <p className="font-medium">{rule.priority}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Category:</span>
              <p className="font-medium">{rule.anchor_category || 'N/A'}</p>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-2">Suggested Items:</h4>
            <div className="flex flex-wrap gap-2">
              {rule.upsell_items?.map((item, idx) => (
                <span key={idx} className="bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 px-3 py-1 rounded-full text-sm">
                  {item.item_name}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Rule Modal (Add/Edit) - Category-based selection
function RuleModal({ rule, menuItems, itemsWithRules, onClose, onSave }) {
  const [step, setStep] = useState(rule ? 2 : 1); // Skip anchor selection if editing
  const [anchorItem, setAnchorItem] = useState(
    rule ? menuItems.find(i => i.id === rule.anchor_item_id) : null
  );
  const [upsellItems, setUpsellItems] = useState(rule?.upsell_items || []);
  const [message, setMessage] = useState(rule?.message || 'You might also like');
  const [frequency, setFrequency] = useState(rule?.frequency || 'every_add');
  const [priority, setPriority] = useState(rule?.priority || 1);
  const [saving, setSaving] = useState(false);

  // Category-based filtering
  const [anchorCategory, setAnchorCategory] = useState('');
  const [upsellCategory, setUpsellCategory] = useState('');

  // Get unique categories from menu items
  const categories = [...new Set(menuItems.map(item => item.category).filter(Boolean))].sort();

  // Filter items for anchor selection (by category)
  const filteredAnchorItems = menuItems.filter(item => {
    if (!anchorCategory) return true; // Show all if no category selected
    return item.category === anchorCategory;
  }).filter(item => !itemsWithRules.has(item.id)); // Exclude items with existing rules

  // Filter items for upsell selection (by category, exclude anchor and already selected)
  const filteredUpsellItems = menuItems.filter(item => {
    if (!upsellCategory) return true; // Show all if no category selected
    return item.category === upsellCategory;
  }).filter(item => 
    item.id !== anchorItem?.id && 
    !upsellItems.some(u => u.item_id === item.id)
  );

  // Group items by category for display
  const getItemsByCategory = (items) => {
    const grouped = {};
    items.forEach(item => {
      const cat = item.category || 'Uncategorized';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(item);
    });
    return grouped;
  };

  const addUpsellItem = (item) => {
    if (upsellItems.length >= 3) {
      toast.error('Maximum 3 upsell items allowed');
      return;
    }
    setUpsellItems([...upsellItems, {
      item_id: item.id,
      item_name: item.name,
      item_category: item.category,
      priority: upsellItems.length + 1
    }]);
  };

  const removeUpsellItem = (itemId) => {
    setUpsellItems(upsellItems.filter(u => u.item_id !== itemId));
  };

  const moveUpsellItem = (index, direction) => {
    const newItems = [...upsellItems];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newItems.length) return;
    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
    // Update priorities
    newItems.forEach((item, idx) => item.priority = idx + 1);
    setUpsellItems(newItems);
  };

  const handleSave = async () => {
    if (!anchorItem) {
      toast.error('Please select an anchor item');
      return;
    }
    if (upsellItems.length === 0) {
      toast.error('Please add at least one upsell item');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        anchor_item_id: anchorItem.id,
        anchor_item_name: anchorItem.name,
        anchor_category: anchorItem.category,
        upsell_items: upsellItems,
        message,
        frequency,
        priority,
        is_active: true
      };

      let data;
      if (rule) {
        const res = await api.put(`/admin/upsells/rules/${anchorItem.id}`, { 
          upsell_items: upsellItems, message, frequency, priority 
        });
        data = res.data;
      } else {
        const res = await api.post('/admin/upsells/rules', payload);
        data = res.data;
      }
      
      toast.success(rule ? 'Rule updated' : 'Rule created');
      onSave(data.rule || payload);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save rule');
    }
    setSaving(false);
  };

  // Count items with rules per category
  const getCategoryRuleCount = (category) => {
    return menuItems.filter(item => 
      item.category === category && itemsWithRules.has(item.id)
    ).length;
  };

  const getCategoryItemCount = (category) => {
    return menuItems.filter(item => item.category === category).length;
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h3 className="text-lg font-semibold">
            {rule ? 'Edit Upsell Rule' : 'Create Upsell Rule'}
          </h3>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Steps */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center gap-4">
            <div className={`flex items-center gap-2 ${step >= 1 ? 'text-primary' : 'text-muted-foreground'}`}>
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${step >= 1 ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>1</span>
              <span className="text-sm font-medium">Select Anchor Item</span>
            </div>
            <div className="h-px flex-1 bg-border" />
            <div className={`flex items-center gap-2 ${step >= 2 ? 'text-primary' : 'text-muted-foreground'}`}>
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${step >= 2 ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>2</span>
              <span className="text-sm font-medium">Select Upsell Items</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {step === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                First, select a category to find the main item that will trigger upsell suggestions:
              </p>
              
              {/* Category Selection for Anchor */}
              <div>
                <label className="text-sm font-medium mb-2 block">Select Category</label>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setAnchorCategory('')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      anchorCategory === ''
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    All Categories
                  </button>
                  {categories.map(cat => {
                    const ruleCount = getCategoryRuleCount(cat);
                    const totalCount = getCategoryItemCount(cat);
                    const availableCount = totalCount - ruleCount;
                    return (
                      <button
                        key={cat}
                        onClick={() => setAnchorCategory(cat)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                          anchorCategory === cat
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground hover:bg-muted/80'
                        }`}
                      >
                        <span className="capitalize">{cat.replace(/-/g, ' ')}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          anchorCategory === cat 
                            ? 'bg-white/20' 
                            : availableCount > 0 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-800'
                        }`}>
                          {availableCount}/{totalCount}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Numbers show available items / total items (items with existing rules are excluded)
                </p>
              </div>

              {/* Items in Selected Category */}
              <div>
                <label className="text-sm font-medium mb-2 block">
                  {anchorCategory ? `Items in ${anchorCategory.replace(/-/g, ' ')}` : 'All Items'} 
                  <span className="text-muted-foreground ml-2">({filteredAnchorItems.length} available)</span>
                </label>
                {filteredAnchorItems.length === 0 ? (
                  <div className="text-center py-8 bg-muted/30 rounded-lg">
                    <p className="text-muted-foreground">
                      {anchorCategory 
                        ? `All items in ${anchorCategory} already have upsell rules configured.`
                        : 'No items available. All items have rules configured.'}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto border rounded-lg p-2">
                    {filteredAnchorItems.map(item => (
                      <button
                        key={item.id}
                        onClick={() => { setAnchorItem(item); setStep(2); }}
                        className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-all ${
                          anchorItem?.id === item.id 
                            ? 'border-primary bg-primary/10' 
                            : 'border-border hover:border-primary/50 hover:bg-muted/30'
                        }`}
                      >
                        {item.image && (
                          <img src={item.image} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="font-medium truncate text-sm">{item.name}</p>
                          <p className="text-xs text-muted-foreground capitalize">{item.category?.replace(/-/g, ' ')}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              {/* Selected Anchor */}
              <div className="flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                {anchorItem?.image && (
                  <img src={anchorItem.image} alt="" className="w-14 h-14 rounded-lg object-cover" />
                )}
                <div className="flex-1">
                  <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">ANCHOR ITEM (triggers upsell)</p>
                  <p className="font-semibold">{anchorItem?.name}</p>
                  <p className="text-sm text-muted-foreground capitalize">{anchorItem?.category?.replace(/-/g, ' ')}</p>
                </div>
                {!rule && (
                  <Button variant="outline" size="sm" onClick={() => setStep(1)}>
                    Change
                  </Button>
                )}
              </div>

              {/* Selected Upsell Items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium">Upsell Items ({upsellItems.length}/3)</h4>
                  {upsellItems.length === 3 && (
                    <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-1 rounded">
                      Maximum reached
                    </span>
                  )}
                </div>
                {upsellItems.length > 0 ? (
                  <div className="space-y-2 mb-4">
                    {upsellItems.map((item, index) => (
                      <div key={item.item_id} className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                        <span className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-xs font-bold">
                          {index + 1}
                        </span>
                        <div className="flex-1">
                          <span className="text-sm font-medium">{item.item_name}</span>
                          {item.item_category && (
                            <span className="text-xs text-muted-foreground ml-2 capitalize">
                              ({item.item_category.replace(/-/g, ' ')})
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => moveUpsellItem(index, -1)} disabled={index === 0}>
                            <ArrowUp className="w-3 h-3" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => moveUpsellItem(index, 1)} disabled={index === upsellItems.length - 1}>
                            <ArrowDown className="w-3 h-3" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeUpsellItem(item.item_id)}>
                            <X className="w-3 h-3 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 bg-muted/30 rounded-lg mb-4 border border-dashed">
                    <p className="text-sm text-muted-foreground">Select up to 3 items to suggest when customer adds {anchorItem?.name}</p>
                  </div>
                )}

                {/* Category Selection for Upsells */}
                {upsellItems.length < 3 && (
                  <>
                    <label className="text-sm font-medium mb-2 block">Browse by Category</label>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <button
                        onClick={() => setUpsellCategory('')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                          upsellCategory === ''
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground hover:bg-muted/80'
                        }`}
                      >
                        All
                      </button>
                      {categories.map(cat => (
                        <button
                          key={cat}
                          onClick={() => setUpsellCategory(cat)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all capitalize ${
                            upsellCategory === cat
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted text-muted-foreground hover:bg-muted/80'
                          }`}
                        >
                          {cat.replace(/-/g, ' ')}
                        </button>
                      ))}
                    </div>

                    {/* Items Grid */}
                    <div className="border rounded-lg p-2 max-h-48 overflow-y-auto">
                      {filteredUpsellItems.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          No items available in this category
                        </p>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {filteredUpsellItems.map(item => (
                            <button
                              key={item.id}
                              onClick={() => addUpsellItem(item)}
                              disabled={upsellItems.length >= 3}
                              className="flex items-center gap-2 p-2 rounded-lg border border-border hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-900/20 text-left text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Plus className="w-4 h-4 text-green-600 flex-shrink-0" />
                              <span className="truncate">{item.name}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Message */}
              <div>
                <label className="text-sm font-medium mb-1 block">Popup Message</label>
                <Input
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="e.g., Pair with a treat?"
                />
                <p className="text-xs text-muted-foreground mt-1">This message appears at the top of the upsell popup</p>
              </div>

              {/* Frequency & Priority */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Frequency</label>
                  <select
                    value={frequency}
                    onChange={(e) => setFrequency(e.target.value)}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    {FREQUENCY_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Priority (1 = highest)</label>
                  <Input
                    type="number"
                    min="1"
                    max="99"
                    value={priority}
                    onChange={(e) => setPriority(parseInt(e.target.value) || 1)}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {step === 2 && (
            <Button onClick={handleSave} disabled={saving || upsellItems.length === 0} className="bg-amber-500 hover:bg-amber-600">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {rule ? 'Update Rule' : 'Create Rule'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Category Rules Tab
// ============================================================================
function CategoryRulesTab({ categoryRules, setCategoryRules, categories, onRefresh }) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [newRule, setNewRule] = useState({
    trigger_categories: [],
    suggest_categories: [],
    message: 'Complete your order',
    priority: 10,
    is_active: true
  });

  const deleteRule = async (ruleId) => {
    if (!confirm('Delete this category rule?')) return;
    try {
      await api.delete(`/admin/upsells/category-rules/${ruleId}`);
      setCategoryRules(categoryRules.filter(r => r.id !== ruleId));
      toast.success('Category rule deleted');
    } catch (err) {
      toast.error('Error deleting rule');
    }
  };

  const createRule = async () => {
    if (newRule.trigger_categories.length === 0 || newRule.suggest_categories.length === 0) {
      toast.error('Please select both trigger and suggest categories');
      return;
    }
    try {
      const res = await api.post('/admin/upsells/category-rules', newRule);
      setCategoryRules([...categoryRules, res.data.rule]);
      setShowAddModal(false);
      setNewRule({
        trigger_categories: [],
        suggest_categories: [],
        message: 'Complete your order',
        priority: 10,
        is_active: true
      });
      toast.success('Category rule created');
    } catch (err) {
      toast.error('Error creating rule');
    }
  };

  const toggleCategory = (cat, type) => {
    const key = type === 'trigger' ? 'trigger_categories' : 'suggest_categories';
    const current = newRule[key];
    if (current.includes(cat)) {
      setNewRule({ ...newRule, [key]: current.filter(c => c !== cat) });
    } else {
      setNewRule({ ...newRule, [key]: [...current, cat] });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="font-medium">Category Fallback Rules</h3>
          <p className="text-sm text-muted-foreground">
            These rules apply when no item-specific rules match the cart contents
          </p>
        </div>
        <Button onClick={() => setShowAddModal(true)} variant="outline">
          <Plus className="w-4 h-4 mr-2" />
          Add Category Rule
        </Button>
      </div>

      {categoryRules.length === 0 ? (
        <div className="text-center py-8 bg-muted/30 rounded-xl border border-dashed">
          <Layers className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No category fallback rules configured</p>
        </div>
      ) : (
        <div className="space-y-3">
          {categoryRules.map(rule => (
            <div key={rule.id} className="p-4 bg-card rounded-xl border">
              <div className="flex justify-between items-start">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-muted-foreground">When cart has:</span>
                    {rule.trigger_categories.map(cat => (
                      <span key={cat} className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded text-sm">
                        {cat}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-muted-foreground">Suggest from:</span>
                    {rule.suggest_categories.map(cat => (
                      <span key={cat} className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-0.5 rounded text-sm">
                        {cat}
                      </span>
                    ))}
                  </div>
                  <p className="text-sm">Message: "{rule.message}"</p>
                </div>
                <Button variant="ghost" size="icon" onClick={() => deleteRule(rule.id)}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl max-w-lg w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Add Category Rule</h3>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Trigger Categories (when cart contains):</label>
                <div className="flex flex-wrap gap-2">
                  {categories.map(cat => (
                    <button
                      key={cat}
                      onClick={() => toggleCategory(cat, 'trigger')}
                      className={`px-3 py-1 rounded-full text-sm border ${
                        newRule.trigger_categories.includes(cat)
                          ? 'bg-blue-500 text-white border-blue-500'
                          : 'border-border hover:border-blue-300'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Suggest Categories:</label>
                <div className="flex flex-wrap gap-2">
                  {categories.map(cat => (
                    <button
                      key={cat}
                      onClick={() => toggleCategory(cat, 'suggest')}
                      className={`px-3 py-1 rounded-full text-sm border ${
                        newRule.suggest_categories.includes(cat)
                          ? 'bg-green-500 text-white border-green-500'
                          : 'border-border hover:border-green-300'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">Message</label>
                <Input
                  value={newRule.message}
                  onChange={(e) => setNewRule({ ...newRule, message: e.target.value })}
                  placeholder="e.g., Add a drink?"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">Priority (higher = lower priority)</label>
                <Input
                  type="number"
                  value={newRule.priority}
                  onChange={(e) => setNewRule({ ...newRule, priority: parseInt(e.target.value) || 10 })}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
              <Button onClick={createRule}>Create Rule</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Settings Tab
// ============================================================================
function SettingsTab({ settings, setSettings }) {
  const [saving, setSaving] = useState(false);

  const updateSetting = async (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    
    setSaving(true);
    try {
      await api.put('/admin/upsells/settings', newSettings);
    } catch (err) {
      toast.error('Error saving settings');
    }
    setSaving(false);
  };

  const SettingToggle = ({ label, desc, settingKey }) => (
    <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
      <div>
        <p className="font-medium">{label}</p>
        <p className="text-sm text-muted-foreground">{desc}</p>
      </div>
      <button
        onClick={() => updateSetting(settingKey, !settings[settingKey])}
        className={`w-12 h-7 rounded-full transition-colors ${settings[settingKey] ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`}
      >
        <span className={`block w-5 h-5 bg-white rounded-full shadow transform transition-transform ${settings[settingKey] ? 'translate-x-6' : 'translate-x-1'}`} />
      </button>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <Settings2 className="w-5 h-5 text-muted-foreground" />
        <h3 className="font-medium">Global Upsell Settings</h3>
        {saving && <Loader2 className="w-4 h-4 animate-spin ml-2" />}
      </div>

      <div className="space-y-3">
        <SettingToggle
          label="Enable Upsells"
          desc="Master switch for all upsell functionality"
          settingKey="enabled"
        />
        <SettingToggle
          label="Show on Website"
          desc="Display upsell popups on the customer ordering website"
          settingKey="show_on_web"
        />
        <SettingToggle
          label="Show on Kiosk"
          desc="Display upsell popups on self-service kiosks"
          settingKey="show_on_kiosk"
        />
        <SettingToggle
          label="Show on POS"
          desc="Display upsell reminders on server POS"
          settingKey="show_on_pos"
        />
        <SettingToggle
          label="Category Fallback"
          desc="Use category rules when no item-specific rules match"
          settingKey="use_category_fallback"
        />
        <SettingToggle
          label="Popular Items Fallback"
          desc="Show popular items if no rules match at all"
          settingKey="popular_items_fallback"
        />
      </div>

      <div className="p-4 bg-muted/30 rounded-lg">
        <label className="font-medium mb-2 block">Max Suggestions</label>
        <Input
          type="number"
          min="1"
          max="6"
          value={settings.max_suggestions || 3}
          onChange={(e) => updateSetting('max_suggestions', parseInt(e.target.value) || 3)}
          className="w-24"
        />
        <p className="text-sm text-muted-foreground mt-1">Maximum number of items to suggest</p>
      </div>
    </div>
  );
}

// ============================================================================
// Help Tab
// ============================================================================
function HelpTab() {
  return (
    <div className="prose dark:prose-invert max-w-none">
      <h3>How Upsells Work</h3>
      
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-5 mb-6">
        <h4 className="text-amber-800 dark:text-amber-200 mt-0 flex items-center gap-2">
          <Info className="w-5 h-5" />
          Priority Order
        </h4>
        <ol className="text-amber-900 dark:text-amber-100 mb-0">
          <li><strong>Item Rules</strong> - Specific items you configure trigger their linked upsells</li>
          <li><strong>Category Rules</strong> - Fallback when no item rules match</li>
          <li><strong>Popular Items</strong> - Last resort shows bestsellers</li>
        </ol>
      </div>

      <h4>Item Rules (Recommended)</h4>
      <p>
        Create rules for your <strong>core/anchor items</strong> - the products customers typically order as their main purchase.
        For each anchor item, specify exactly which items should be suggested.
      </p>
      <p>Example:</p>
      <ul>
        <li><strong>Flat White</strong> (anchor) → suggests <em>Blueberry Muffin, Croissant, Cookie</em></li>
        <li><strong>Breakfast Wrap</strong> (anchor) → suggests <em>Fresh OJ, Latte, Iced Coffee</em></li>
      </ul>

      <h4>Frequency Options</h4>
      <table>
        <thead>
          <tr>
            <th>Option</th>
            <th>Behavior</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Every Time Added</strong></td>
            <td>Popup shows each time this item is added to cart</td>
          </tr>
          <tr>
            <td><strong>First Add Only</strong></td>
            <td>Popup shows only the first time (won't repeat if they add another)</td>
          </tr>
          <tr>
            <td><strong>Once Per Session</strong></td>
            <td>Shows once per browsing session, even across different orders</td>
          </tr>
        </tbody>
      </table>

      <h4>Priority</h4>
      <p>
        If multiple anchor items are in the cart, the <strong>lowest priority number</strong> takes precedence.
        Priority 1 is highest. This determines which rule's message and suggestions are shown.
      </p>

      <h4>Category Rules (Fallback)</h4>
      <p>
        These are backup rules based on product categories. They only activate when no item-specific rules match.
        Example: "If cart has Breakfast items, suggest Coffee and Juice"
      </p>

      <h4>Where Upsells Appear</h4>
      <ul>
        <li><strong>Customer Website</strong> - Popup after adding to cart</li>
        <li><strong>Kiosk</strong> - Full-screen popup after item added</li>
        <li><strong>Server POS</strong> - Collapsible reminder banner with script suggestions</li>
      </ul>
    </div>
  );
}


// ============================================================================
// Analytics Tab
// ============================================================================
function AnalyticsTab() {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [days, setDays] = useState(30);

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/upsells/analytics', { params: { days } });
      setAnalytics(res.data);
    } catch (err) {
      toast.error('Error loading analytics');
    }
    setLoading(false);
  }, [days]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const resetAnalytics = async () => {
    if (!confirm('Are you sure you want to reset ALL analytics data? This cannot be undone.')) return;
    
    try {
      await api.post('/admin/upsells/analytics/reset');
      toast.success('Analytics data reset');
      loadAnalytics();
    } catch (err) {
      toast.error('Error resetting analytics');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="ml-3 text-muted-foreground">Loading analytics...</span>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12">
        <BarChart3 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
        <p className="text-muted-foreground">No analytics data available</p>
        <Button onClick={loadAnalytics} variant="outline" className="mt-4">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>
    );
  }

  const { summary, top_performing_rules, top_converting_items, performance_by_source, daily_trend } = analytics;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            Upsell Performance Analytics
          </h3>
          <p className="text-sm text-muted-foreground">
            Track which upsell rules are most effective at driving conversions
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Time Range Selector */}
          <div className="flex items-center gap-2 bg-muted rounded-lg p-1">
            {[7, 30, 90].map(d => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  days === d 
                    ? 'bg-background text-foreground shadow-sm' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {d} Days
              </button>
            ))}
          </div>
          <Button onClick={loadAnalytics} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
          <Button onClick={resetAnalytics} variant="ghost" size="sm" className="text-red-500 hover:text-red-600">
            Reset Data
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-card rounded-xl border p-4">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <Eye className="w-4 h-4" />
            <span className="text-sm">Impressions</span>
          </div>
          <p className="text-2xl font-bold text-foreground">{summary?.total_impressions?.toLocaleString() || 0}</p>
          <p className="text-xs text-muted-foreground mt-1">Times shown</p>
        </div>
        <div className="bg-card rounded-xl border p-4">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <MousePointerClick className="w-4 h-4" />
            <span className="text-sm">Conversions</span>
          </div>
          <p className="text-2xl font-bold text-green-600">{summary?.total_conversions?.toLocaleString() || 0}</p>
          <p className="text-xs text-muted-foreground mt-1">Items added</p>
        </div>
        <div className="bg-card rounded-xl border p-4">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <Target className="w-4 h-4" />
            <span className="text-sm">Conversion Rate</span>
          </div>
          <p className="text-2xl font-bold text-primary">{summary?.overall_conversion_rate || 0}%</p>
          <p className="text-xs text-muted-foreground mt-1">Added / Shown</p>
        </div>
        <div className="bg-card rounded-xl border p-4">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <X className="w-4 h-4" />
            <span className="text-sm">Dismissed</span>
          </div>
          <p className="text-2xl font-bold text-muted-foreground">{summary?.total_dismissed?.toLocaleString() || 0}</p>
          <p className="text-xs text-muted-foreground mt-1">Closed without adding</p>
        </div>
      </div>

      {/* Performance by Source */}
      {performance_by_source && Object.keys(performance_by_source).length > 0 && (
        <div className="bg-card rounded-xl border p-5">
          <h4 className="font-medium mb-4 flex items-center gap-2">
            <Layers className="w-4 h-4 text-muted-foreground" />
            Performance by Source
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {Object.entries(performance_by_source).map(([source, data]) => (
              <div key={source} className="bg-muted/30 rounded-lg p-4">
                <p className="font-medium capitalize text-foreground">{source}</p>
                <div className="mt-2 space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Shown:</span>
                    <span>{data.shown?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Added:</span>
                    <span className="text-green-600">{data.added?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between font-medium">
                    <span className="text-muted-foreground">Rate:</span>
                    <span className="text-primary">{data.conversion_rate?.toFixed(1)}%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Performing Rules */}
        <div className="bg-card rounded-xl border p-5">
          <h4 className="font-medium mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-green-500" />
            Top Performing Rules
            <span className="ml-auto text-xs text-muted-foreground">by conversion rate</span>
          </h4>
          {top_performing_rules?.length > 0 ? (
            <div className="space-y-3">
              {top_performing_rules.slice(0, 5).map((rule, idx) => (
                <div key={rule.anchor_item_id} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    idx === 0 ? 'bg-yellow-500 text-white' : 
                    idx === 1 ? 'bg-gray-400 text-white' :
                    idx === 2 ? 'bg-amber-600 text-white' :
                    'bg-muted text-muted-foreground'
                  }`}>
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{rule.anchor_item_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {rule.impressions || 0} shown • {rule.conversions || 0} added
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`font-bold ${rule.conversion_rate > 10 ? 'text-green-600' : 'text-foreground'}`}>
                      {rule.conversion_rate?.toFixed(1)}%
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">No data yet - rules need impressions first</p>
          )}
        </div>

        {/* Top Converting Items */}
        <div className="bg-card rounded-xl border p-5">
          <h4 className="font-medium mb-4 flex items-center gap-2">
            <ShoppingCart className="w-4 h-4 text-amber-500" />
            Most Added Items
            <span className="ml-auto text-xs text-muted-foreground">from upsells</span>
          </h4>
          {top_converting_items?.length > 0 ? (
            <div className="space-y-3">
              {top_converting_items.slice(0, 5).map((item, idx) => (
                <div key={item.item_id} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    idx === 0 ? 'bg-yellow-500 text-white' : 
                    idx === 1 ? 'bg-gray-400 text-white' :
                    idx === 2 ? 'bg-amber-600 text-white' :
                    'bg-muted text-muted-foreground'
                  }`}>
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{item.name}</p>
                    <p className="text-xs text-muted-foreground">{item.category}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-green-600">{item.conversions}</p>
                    <p className="text-xs text-muted-foreground">added</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">No items added from upsells yet</p>
          )}
        </div>
      </div>

      {/* Daily Trend */}
      {daily_trend?.length > 0 && (
        <div className="bg-card rounded-xl border p-5">
          <h4 className="font-medium mb-4 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            7-Day Trend
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="text-left py-2 px-3">Date</th>
                  <th className="text-right py-2 px-3">Shown</th>
                  <th className="text-right py-2 px-3">Added</th>
                  <th className="text-right py-2 px-3">Rate</th>
                </tr>
              </thead>
              <tbody>
                {daily_trend.map((day) => {
                  const rate = day.shown > 0 ? ((day.added / day.shown) * 100).toFixed(1) : 0;
                  return (
                    <tr key={day.date} className="border-b border-muted">
                      <td className="py-2 px-3 font-medium">{day.date}</td>
                      <td className="text-right py-2 px-3">{day.shown}</td>
                      <td className="text-right py-2 px-3 text-green-600">{day.added}</td>
                      <td className="text-right py-2 px-3 font-medium text-primary">{rate}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
        <h4 className="font-medium text-amber-800 dark:text-amber-200 flex items-center gap-2 mb-2">
          <Info className="w-4 h-4" />
          How Analytics Work
        </h4>
        <ul className="text-sm text-amber-700 dark:text-amber-300 space-y-1">
          <li>• <strong>Impressions</strong> are tracked when an upsell popup is shown to a customer</li>
          <li>• <strong>Conversions</strong> are tracked when a customer clicks to add an upsell item</li>
          <li>• <strong>Dismissed</strong> counts when customers close the popup without adding</li>
          <li>• Data is tracked separately for Website, Kiosk, and POS sources</li>
        </ul>
      </div>
    </div>
  );
}


// ============================================================================
// AI Suggestions Tab
// ============================================================================
function SuggestionsTab({ onRuleCreated }) {
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [stats, setStats] = useState(null);
  const [meta, setMeta] = useState({});
  const [applying, setApplying] = useState(null);
  const [dismissing, setDismissing] = useState(null);

  // Settings
  const [days, setDays] = useState(90);
  const [minOrders, setMinOrders] = useState(5);
  const [minConfidence, setMinConfidence] = useState(10);

  const loadSuggestions = useCallback(async () => {
    setLoading(true);
    try {
      const [suggestionsRes, statsRes] = await Promise.all([
        api.get('/admin/upsells/suggestions', { 
          params: { days, min_orders: minOrders, min_confidence: minConfidence }
        }),
        api.get('/admin/upsells/suggestions/stats')
      ]);
      
      setSuggestions(suggestionsRes.data.suggestions || []);
      setMeta({
        total_found: suggestionsRes.data.total_found,
        orders_analyzed: suggestionsRes.data.orders_analyzed,
        existing_rules: suggestionsRes.data.existing_rules_count
      });
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Failed to load suggestions');
    }
    setLoading(false);
  }, [days, minOrders, minConfidence]);

  useEffect(() => {
    loadSuggestions();
  }, [loadSuggestions]);

  const applySuggestion = async (suggestion) => {
    setApplying(suggestion.anchor_item_id);
    try {
      const res = await api.post('/admin/upsells/suggestions/apply', suggestion);
      toast.success(`Rule created for "${suggestion.anchor_item_name}"`);
      onRuleCreated(res.data.rule);
      // Remove from suggestions
      setSuggestions(suggestions.filter(s => s.anchor_item_id !== suggestion.anchor_item_id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create rule');
    }
    setApplying(null);
  };

  const dismissSuggestion = async (suggestion) => {
    setDismissing(suggestion.anchor_item_id);
    try {
      await api.post('/admin/upsells/suggestions/dismiss', null, {
        params: { anchor_item_id: suggestion.anchor_item_id }
      });
      toast.success('Suggestion dismissed');
      setSuggestions(suggestions.filter(s => s.anchor_item_id !== suggestion.anchor_item_id));
    } catch (err) {
      toast.error('Error dismissing suggestion');
    }
    setDismissing(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="ml-3 text-muted-foreground">Analyzing purchase patterns...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-purple-500" />
            AI-Powered Rule Suggestions
          </h3>
          <p className="text-sm text-muted-foreground">
            Automatically discover items frequently bought together that could be upsell opportunities
          </p>
        </div>
        <Button onClick={loadSuggestions} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-1" />
          Refresh
        </Button>
      </div>

      {/* Stats Summary */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-card rounded-xl border p-4">
            <p className="text-sm text-muted-foreground">Orders Analyzed</p>
            <p className="text-2xl font-bold text-foreground">{meta.orders_analyzed?.toLocaleString() || 0}</p>
          </div>
          <div className="bg-card rounded-xl border p-4">
            <p className="text-sm text-muted-foreground">Suggestions Found</p>
            <p className="text-2xl font-bold text-purple-600">{meta.total_found || 0}</p>
          </div>
          <div className="bg-card rounded-xl border p-4">
            <p className="text-sm text-muted-foreground">Auto-Created Rules</p>
            <p className="text-2xl font-bold text-green-600">{stats.auto_created_rules || 0}</p>
          </div>
          <div className="bg-card rounded-xl border p-4">
            <p className="text-sm text-muted-foreground">Existing Rules</p>
            <p className="text-2xl font-bold text-foreground">{meta.existing_rules || 0}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-muted/30 rounded-xl p-4">
        <h4 className="font-medium mb-3 flex items-center gap-2">
          <Settings2 className="w-4 h-4" />
          Analysis Settings
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">Time Period</label>
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value))}
              className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
            >
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
              <option value={90}>Last 90 days</option>
              <option value={180}>Last 6 months</option>
              <option value={365}>Last year</option>
            </select>
          </div>
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">Min Orders with Item</label>
            <Input
              type="number"
              value={minOrders}
              onChange={(e) => setMinOrders(parseInt(e.target.value) || 5)}
              min="1"
              max="100"
            />
          </div>
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">Min Confidence %</label>
            <Input
              type="number"
              value={minConfidence}
              onChange={(e) => setMinConfidence(parseInt(e.target.value) || 10)}
              min="1"
              max="100"
            />
          </div>
        </div>
      </div>

      {/* Suggestions List */}
      {suggestions.length === 0 ? (
        <div className="text-center py-12 bg-muted/30 rounded-xl border border-dashed">
          <Lightbulb className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No New Suggestions</h3>
          <p className="text-muted-foreground max-w-md mx-auto">
            {meta.orders_analyzed === 0 
              ? "Not enough order data to analyze yet. Suggestions will appear as customers place more orders."
              : "Great job! You've already configured rules for frequently co-purchased items. Try lowering the confidence threshold to see more suggestions."}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Found <strong>{suggestions.length}</strong> potential upsell opportunities based on purchase patterns
          </p>
          
          {suggestions.map((suggestion) => (
            <SuggestionCard
              key={suggestion.anchor_item_id}
              suggestion={suggestion}
              onApply={() => applySuggestion(suggestion)}
              onDismiss={() => dismissSuggestion(suggestion)}
              applying={applying === suggestion.anchor_item_id}
              dismissing={dismissing === suggestion.anchor_item_id}
            />
          ))}
        </div>
      )}

      {/* Auto-Created Rule Performance */}
      {stats?.auto_rule_performance?.length > 0 && (
        <div className="bg-card rounded-xl border p-5">
          <h4 className="font-medium mb-4 flex items-center gap-2">
            <Zap className="w-4 h-4 text-yellow-500" />
            Auto-Created Rule Performance
            <span className="ml-auto text-xs text-muted-foreground">How are suggested rules performing?</span>
          </h4>
          <div className="space-y-3">
            {stats.auto_rule_performance.map((rule, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                <div className="flex-1">
                  <p className="font-medium">{rule.anchor_item_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {rule.impressions} shown • {rule.conversions} added • Original confidence: {rule.original_confidence}%
                  </p>
                </div>
                <div className="text-right">
                  <p className={`font-bold ${rule.conversion_rate > 10 ? 'text-green-600' : 'text-foreground'}`}>
                    {rule.conversion_rate}%
                  </p>
                  <p className="text-xs text-muted-foreground">conversion</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl p-4">
        <h4 className="font-medium text-purple-800 dark:text-purple-200 flex items-center gap-2 mb-2">
          <Info className="w-4 h-4" />
          How AI Suggestions Work
        </h4>
        <ul className="text-sm text-purple-700 dark:text-purple-300 space-y-1">
          <li>• Analyzes your order history to find items frequently bought together</li>
          <li>• <strong>Confidence score</strong> = % of orders with item A that also contain item B</li>
          <li>• Only suggests items that don't already have upsell rules configured</li>
          <li>• Click <strong>Apply</strong> to instantly create the rule, or <strong>Dismiss</strong> to hide it</li>
        </ul>
      </div>
    </div>
  );
}

// Suggestion Card Component
function SuggestionCard({ suggestion, onApply, onDismiss, applying, dismissing }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-card rounded-xl border shadow-sm overflow-hidden">
      {/* Header */}
      <div className="p-4">
        <div className="flex items-start gap-4">
          {suggestion.anchor_image && (
            <img 
              src={suggestion.anchor_image} 
              alt="" 
              className="w-16 h-16 rounded-lg object-cover"
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-foreground">{suggestion.anchor_item_name}</h3>
              <span className="bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded-full text-xs">
                {suggestion.confidence_score}% confidence
              </span>
            </div>
            <p className="text-sm text-muted-foreground">
              {suggestion.anchor_category} • Ordered {suggestion.total_orders_with_anchor} times
            </p>
            <p className="text-sm text-muted-foreground mt-1">{suggestion.reason}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={onDismiss}
              disabled={dismissing}
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-red-500"
            >
              {dismissing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ThumbsDown className="w-4 h-4" />}
            </Button>
            <Button
              onClick={onApply}
              disabled={applying}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {applying ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Zap className="w-4 h-4 mr-2" />
              )}
              Apply Rule
            </Button>
          </div>
        </div>

        {/* Suggested Upsells */}
        <div className="mt-4">
          <button 
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {suggestion.suggested_upsells.length} suggested upsell items
          </button>
          
          {expanded && (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {suggestion.suggested_upsells.map((upsell, idx) => (
                <div key={upsell.item_id} className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg">
                  <span className="w-5 h-5 rounded-full bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 flex items-center justify-center text-xs font-bold">
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{upsell.item_name}</p>
                    <p className="text-xs text-muted-foreground">{upsell.item_category}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-purple-600">{upsell.confidence}%</p>
                    <p className="text-xs text-muted-foreground">{upsell.co_purchase_count}x</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
